import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import { PenLine, Trash2, Lock, Plus, Check, AlertTriangle, X } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

function isWithin24h(createdAt) {
  if (!createdAt) return false;
  return (Date.now() - new Date(createdAt).getTime()) < 24 * 60 * 60 * 1000;
}

export default function ErpJournal() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney, currencySymbol } = useTenantFormat();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);

  // Form state
  const [date, setDate] = useState(todayISO());
  const [desc, setDesc] = useState('');
  const [ref, setRef] = useState('');
  const [lines, setLines] = useState([
    { account_name: '', account_type: 'asset', debit_rupees: '', credit_rupees: '' },
    { account_name: '', account_type: 'asset', debit_rupees: '', credit_rupees: '' },
  ]);
  const [saving, setSaving] = useState(false);

  const loadEntries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.erpJournalList(token);
      setEntries(r?.items || []);
    } catch (e) {
      if (e.status === 403) return;
      toast.show(e.message || 'Failed to load journal', 'error');
    } finally { setLoading(false); }
  }, [token, toast]);

  const loadAccounts = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api.erpLedgerAccounts(token);
      setAccounts(r?.accounts || []);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { loadEntries(); loadAccounts(); }, [loadEntries, loadAccounts]);

  const debitTotal = lines.reduce((s, l) => s + (parseFloat(l.debit_rupees) || 0), 0);
  const creditTotal = lines.reduce((s, l) => s + (parseFloat(l.credit_rupees) || 0), 0);
  const balanced = debitTotal > 0 && debitTotal === creditTotal;
  const canSave = desc.trim() && balanced && lines.every(l => l.account_name.trim());

  const updateLine = (i, field, val) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  };

  const removeLine = (i) => {
    if (lines.length <= 2) return;
    setLines(prev => prev.filter((_, idx) => idx !== i));
  };

  const addLine = () => {
    setLines(prev => [...prev, { account_name: '', account_type: 'asset', debit_rupees: '', credit_rupees: '' }]);
  };

  const resetForm = () => {
    setDate(todayISO());
    setDesc('');
    setRef('');
    setLines([
      { account_name: '', account_type: 'asset', debit_rupees: '', credit_rupees: '' },
      { account_name: '', account_type: 'asset', debit_rupees: '', credit_rupees: '' },
    ]);
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const body = {
        entry_date: date,
        description: desc.trim(),
        reference: ref.trim() || undefined,
        lines: lines.map(l => ({
          account_name: l.account_name.trim(),
          account_type: l.account_type,
          debit_rupees: parseFloat(l.debit_rupees) || 0,
          credit_rupees: parseFloat(l.credit_rupees) || 0,
        })),
      };
      await api.erpJournalCreate(token, body);
      toast.show('Journal entry posted', 'success');
      resetForm();
      loadEntries();
    } catch (e) {
      toast.show(e.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try {
      await api.erpJournalDelete(token, entry.entry_id);
      toast.show('Entry deleted', 'success');
      loadEntries();
    } catch (e) {
      toast.show(e.message || 'Delete failed', 'error');
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="erp-journal-page">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Entries list */}
        <div className="flex-[3] min-w-0">
          <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Journal Entries</h2>
          <div data-testid="erp-journal-list" className="space-y-2">
            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : entries.length === 0 ? (
              <GlassCard dark className="p-8 text-center">
                <PenLine size={28} className="text-white/20 mx-auto mb-3" />
                <p className="text-white/40 text-sm">No journal entries yet. Create one →</p>
              </GlassCard>
            ) : (
              entries.map(e => (
                <GlassCard dark key={e.entry_id} className="p-4" testId={`erp-journal-entry-${e.entry_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white/40 text-[10px] font-bold tabular-nums">{fmtDate(e.entry_date)}</span>
                        {e.reference && (
                          <span className="px-2 py-0.5 rounded bg-white/8 text-white/40 text-[10px] font-bold">REF: {e.reference}</span>
                        )}
                      </div>
                      <p className="text-white font-semibold text-sm truncate">{e.description}</p>
                      <div className="flex gap-4 mt-1">
                        <span className="text-emerald-400 text-xs tabular-nums font-bold">Dr {formatMoney(e.debit_total)}</span>
                        <span className="text-red-400 text-xs tabular-nums font-bold">Cr {formatMoney(e.credit_total)}</span>
                      </div>
                    </div>
                    {isWithin24h(e.created_at) ? (
                      <button
                        onClick={() => handleDelete(e)}
                        data-testid={`erp-journal-delete-${e.entry_id}`}
                        className="p-2 rounded-lg text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors flex-shrink-0"
                        title="Delete entry"
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : (
                      <div
                        className="p-2 rounded-lg text-white/20 cursor-not-allowed flex-shrink-0"
                        title="Locked — post a reversing entry"
                        data-testid={`erp-journal-delete-${e.entry_id}`}
                      >
                        <Lock size={14} />
                      </div>
                    )}
                  </div>
                </GlassCard>
              ))
            )}
          </div>
        </div>

        {/* Right: New Entry Form */}
        <div className="flex-[2] min-w-0">
          <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">New Entry</h2>
          <GlassCard dark className="p-5 space-y-4" testId="erp-journal-new-form">
            {/* Date */}
            <div>
              <label className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full h-10 px-3 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB]/40"
                data-testid="erp-journal-date"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-1">Description *</label>
              <input
                type="text"
                value={desc}
                onChange={e => setDesc(e.target.value)}
                placeholder="e.g. Cash injection from owner"
                className="w-full h-10 px-3 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]/40"
                data-testid="erp-journal-desc"
              />
            </div>

            {/* Reference */}
            <div>
              <label className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-1">Reference</label>
              <input
                type="text"
                value={ref}
                onChange={e => setRef(e.target.value)}
                placeholder="Optional"
                className="w-full h-10 px-3 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]/40"
                data-testid="erp-journal-ref"
              />
            </div>

            {/* Lines */}
            <div>
              <label className="text-white/40 text-[10px] font-bold uppercase tracking-wider block mb-2">Lines</label>
              <div className="space-y-2">
                {lines.map((line, i) => (
                  <JournalLine
                    key={i}
                    index={i}
                    line={line}
                    accounts={accounts}
                    currencySymbol={currencySymbol}
                    onChange={updateLine}
                    onRemove={removeLine}
                    canRemove={lines.length > 2}
                  />
                ))}
              </div>
              <button
                onClick={addLine}
                data-testid="erp-journal-add-line"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2563EB]/10 text-[#2563EB] text-xs font-bold border border-[#2563EB]/20 hover:bg-[#2563EB]/20 transition-colors"
              >
                <Plus size={12} /> Add line
              </button>
            </div>

            {/* Balance bar */}
            <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border ${balanced ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`} data-testid="erp-journal-balance-bar">
              <div className="flex gap-4">
                <span className="text-white text-xs font-bold tabular-nums">Dr: {formatMoney(debitTotal)}</span>
                <span className="text-white text-xs font-bold tabular-nums">Cr: {formatMoney(creditTotal)}</span>
              </div>
              {balanced ? (
                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-bold">
                  <Check size={14} /> Balanced
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-red-400 text-xs font-bold">
                  <AlertTriangle size={14} /> Unbalanced
                </span>
              )}
            </div>

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              data-testid="erp-journal-save-btn"
              className="w-full h-11 rounded-xl bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white font-bold text-sm hover:brightness-110 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check size={16} />}
              {saving ? 'Posting...' : 'Post Entry'}
            </button>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}

function JournalLine({ index, line, accounts, currencySymbol, onChange, onRemove, canRemove }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filtered = accounts.filter(a =>
    a.account_name.toLowerCase().includes((line.account_name || '').toLowerCase())
  ).slice(0, 8);

  return (
    <div className="flex gap-1.5 items-start">
      {/* Account name autocomplete */}
      <div className="flex-1 relative min-w-0">
        <input
          type="text"
          value={line.account_name}
          onChange={e => { onChange(index, 'account_name', e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Account"
          className="w-full h-9 px-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-white text-xs placeholder:text-white/30 focus:outline-none focus:border-[#2563EB]/40"
          data-testid={`erp-journal-line-account-${index}`}
        />
        {showSuggestions && filtered.length > 0 && line.account_name && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#0B1220] border border-white/15 rounded-lg shadow-xl max-h-40 overflow-y-auto">
            {filtered.map(a => (
              <button
                key={a.account_name}
                onMouseDown={() => {
                  onChange(index, 'account_name', a.account_name);
                  onChange(index, 'account_type', a.account_type);
                  setShowSuggestions(false);
                }}
                className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/10 transition-colors flex items-center justify-between"
              >
                <span>{a.account_name}</span>
                <span className="text-white/30 text-[10px] uppercase">{a.account_type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Type */}
      <select
        value={line.account_type}
        onChange={e => onChange(index, 'account_type', e.target.value)}
        className="w-24 h-9 px-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-white text-[10px] focus:outline-none focus:border-[#2563EB]/40"
        data-testid={`erp-journal-line-type-${index}`}
      >
        {['asset', 'liability', 'equity', 'revenue', 'expense'].map(t => (
          <option key={t} value={t} className="bg-[#0B1220] text-white">{t}</option>
        ))}
      </select>

      {/* Debit */}
      <input
        type="number"
        min="0"
        step="0.01"
        value={line.debit_rupees}
        onChange={e => onChange(index, 'debit_rupees', e.target.value)}
        placeholder={`Debit ${currencySymbol}`}
        className="w-24 h-9 px-2 rounded-lg bg-white/[0.05] border border-white/10 text-white text-xs tabular-nums text-right placeholder:text-white/30 focus:outline-none focus:border-emerald-500/40"
        data-testid={`erp-journal-line-debit-${index}`}
      />

      {/* Credit */}
      <input
        type="number"
        min="0"
        step="0.01"
        value={line.credit_rupees}
        onChange={e => onChange(index, 'credit_rupees', e.target.value)}
        placeholder={`Credit ${currencySymbol}`}
        className="w-24 h-9 px-2 rounded-lg bg-white/[0.05] border border-white/10 text-white text-xs tabular-nums text-right placeholder:text-white/30 focus:outline-none focus:border-red-500/40"
        data-testid={`erp-journal-line-credit-${index}`}
      />

      {/* Remove */}
      <button
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        className="p-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-20 disabled:cursor-not-allowed flex-shrink-0"
        data-testid={`erp-journal-line-remove-${index}`}
      >
        <X size={12} />
      </button>
    </div>
  );
}


